/**
 * Permission Mapping Configuration
 * 
 * Maps UI elements (tabs, actions, buttons) to required backend permissions.
 * Based on analysis of backend endpoints and frontend API calls.
 */

// Organization tab permissions (from Organization.jsx tabs array)
export const ORGANIZATION_TAB_PERMISSIONS = {
  'authorization': { resource: 'user_roles', action: 'read' }, // RBAC management (new system)
  'memory': { resource: 'memories', action: 'read' }, // Memory Context - requires read permission
  'llm-config': { resource: 'llm_config', action: 'read' }, // LLM Configuration - requires read permission
  'information-sources': { resource: 'rag', action: 'list' }, // RAG documents + Bedrock + DB connections + Personal Context
  'usage-limits': { resource: 'metrics', action: 'admin' }, // Usage limits management
  'agents': { resource: 'agents', action: 'read' }, // Agents management
  'metrics': { resource: 'metrics', action: 'read' }, // Usage metrics and costs
  'backups': { resource: 'backups', action: 'read' } // Backups management
};

// Chat interface permissions (from ChatInterface.jsx actions)
export const CHAT_ACTION_PERMISSIONS = {
  'send_message': { resource: 'supervisor', action: 'query' }, // /api/supervisor/process
  'extend_query': { resource: 'supervisor', action: 'query' }, // /api/supervisor/extend-query
  'route_query': { resource: 'supervisor', action: 'route' }, // /api/supervisor/route-query
  'use_rag': { resource: 'rag', action: 'query' }, // /api/rag/query
  'use_bedrock': { resource: 'bedrock', action: 'invoke' }, // /api/bedrock/query
  'use_customer_db': { resource: 'database_connections', action: 'read' } // /api/customer-database/query
};

// Chat session permissions (backend enforced)
export const CHAT_PERMISSIONS = {
  'create_session': { resource: 'chat', action: 'write' }, // POST /api/chat/sessions
  'send_message': { resource: 'chat', action: 'write' }, // POST /api/chat/sessions/{id}/messages
  'view_sessions': { resource: 'chat', action: 'list' }, // GET /api/chat/sessions
  'view_session': { resource: 'chat', action: 'read' }, // GET /api/chat/sessions/{id}
  'update_session': { resource: 'chat', action: 'write' }, // PUT /api/chat/sessions/{id}
  'delete_session': { resource: 'chat', action: 'delete' } // DELETE /api/chat/sessions/{id}
};

// Memory/Knowledge management permissions (used across app)
export const MEMORY_PERMISSIONS = {
  'view': { resource: 'memories', action: 'list' }, // /api/memories
  'create': { resource: 'memories', action: 'write' }, // POST /api/memories
  'update': { resource: 'memories', action: 'write' }, // PUT /api/memories/{id}
  'delete': { resource: 'memories', action: 'delete' }, // DELETE /api/memories/{id}
  'stats': { resource: 'memories', action: 'stats' } // /api/memories/stats
};

export const KNOWLEDGE_PERMISSIONS = {
  'view': { resource: 'knowledge', action: 'list' }, // /api/knowledge/documents
  'add': { resource: 'knowledge', action: 'write' }, // POST /api/knowledge/documents
  'connect': { resource: 'knowledge', action: 'connect' }, // PUT /api/knowledge/documents/{id}/connect
  'disconnect': { resource: 'knowledge', action: 'disconnect' }, // PUT /api/knowledge/documents/{id}/disconnect
  'stats': { resource: 'knowledge', action: 'stats' } // /api/knowledge/stats
};

// RAG system permissions (InformationSources component)
export const RAG_PERMISSIONS = {
  'query': { resource: 'rag', action: 'query' }, // /api/rag/query
  'upload': { resource: 'rag', action: 'upload' }, // /api/rag/documents/upload
  'list': { resource: 'rag', action: 'list' }, // /api/rag/documents
  'delete': { resource: 'rag', action: 'delete' }, // DELETE /api/rag/documents/{id}
  'stats': { resource: 'rag', action: 'stats' } // /api/rag/statistics
};

// Authorization/RBAC permissions (AuthorizationManagement component)
export const RBAC_PERMISSIONS = {
  'view_users': { resource: 'users', action: 'list' }, // /api/auth/users
  'assign_roles': { resource: 'users', action: 'write' }, // /api/auth/users/{sub}/roles/{role}
  'view_roles': { resource: 'user_roles', action: 'list' }, // /api/auth/roles
  'create_role': { resource: 'user_roles', action: 'write' }, // POST /api/auth/roles
  'update_role': { resource: 'user_roles', action: 'write' }, // PUT /api/auth/roles/{id}
  'delete_role': { resource: 'user_roles', action: 'delete' }, // DELETE /api/auth/roles/{id}
  'view_policies': { resource: 'user_roles', action: 'read' }, // /api/auth/policies (always readable)
};

// Metrics permissions (Metrics component)
export const METRICS_PERMISSIONS = {
  'view_summary': { resource: 'metrics', action: 'read' }, // /api/metrics/users/summary
  'view_details': { resource: 'metrics', action: 'read' }, // /api/metrics/users/{id}/details
  'sync_cognito': { resource: 'cognito', action: 'sync' } // /api/metrics/cognito/sync
};

